import { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import Swal from 'sweetalert2';
import {
  useGetExercisesQuery,
  useCreateExerciseMutation,
  useUpdateExerciseMutation,
  useDeleteExerciseMutation,
  useGetFieldsQuery,
  useDeleteFieldMutation,
  useReorderFieldsMutation
} from '../../../Services/PathwayAdminAPI';
import FieldConfigurator from './FieldConfigurator';
import '../../../Styles/ExerciseBuilder.css';

const ExerciseBuilder = ({ show, onHide, stepId, stepData }) => {
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [isEditingExercise, setIsEditingExercise] = useState(false);
  const [exerciseFormData, setExerciseFormData] = useState({
    title: '',
    description: '',
  });
  const [errors, setErrors] = useState({});
  const [showFieldConfigurator, setShowFieldConfigurator] = useState(false);
  const [editingField, setEditingField] = useState(null);

  const { data: exercisesData, isLoading: exercisesLoading } = useGetExercisesQuery(stepId, {
    skip: !stepId,
  });
  const { data: fieldsData } = useGetFieldsQuery(selectedExercise?.id, {
    skip: !selectedExercise?.id,
  });
  
  // Get step number from stepData prop or from the step query
  const currentStepNumber = stepData?.step_number;
  const [createExercise, { isLoading: isCreating }] = useCreateExerciseMutation();
  const [updateExercise, { isLoading: isUpdating }] = useUpdateExerciseMutation();
  const [deleteExercise] = useDeleteExerciseMutation();
  const [deleteField] = useDeleteFieldMutation();
  const [reorderFields] = useReorderFieldsMutation();

  const exercises = exercisesData?.data || [];
  const fields = fieldsData?.data || [];
  const isLoading = isCreating || isUpdating;

  useEffect(() => {
    if (!show) {
      setSelectedExercise(null);
      setIsEditingExercise(false);
      setExerciseFormData({ title: '', description: '' });
      setErrors({});
      setShowFieldConfigurator(false);
      setEditingField(null);
    }
  }, [show]);

  const handleNewExercise = () => {
    setSelectedExercise(null);
    setIsEditingExercise(true);
    setExerciseFormData({ title: '', description: '' });
    setErrors({});
  };

  const handleEditExercise = (exercise) => {
    setSelectedExercise(exercise);
    setIsEditingExercise(true);
    setExerciseFormData({
      title: exercise.title || '',
      description: exercise.description || '',
    });
    setErrors({});
  };

  const handleCancelEdit = () => {
    setIsEditingExercise(false);
    setExerciseFormData({ title: '', description: '' });
    setErrors({});
  };

  const validateExerciseForm = () => {
    const newErrors = {};
    if (!exerciseFormData.title.trim()) {
      newErrors.title = 'Exercise title is required';
    } else if (exerciseFormData.title.length > 500) {
      newErrors.title = 'Title must not exceed 500 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleExerciseSubmit = async (e) => {
    e.preventDefault();
    if (!validateExerciseForm()) return;

    try {
      const submitData = {
        step_id: stepId,
        title: exerciseFormData.title,
        description: exerciseFormData.description,
        order_index: selectedExercise ? selectedExercise.order_index : exercises.length,
      };

      let response;
      if (selectedExercise) {
        response = await updateExercise({
          id: selectedExercise.id,
          ...submitData,
        }).unwrap();
      } else {
        response = await createExercise(submitData).unwrap();
      }

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: response.message || `Exercise ${selectedExercise ? 'updated' : 'created'} successfully`,
        timer: 2000,
        showConfirmButton: false,
        confirmButtonColor: '#FD9C00',
      });

      setIsEditingExercise(false);
      setExerciseFormData({ title: '', description: '' });
      if (!selectedExercise && response.data) {
        setSelectedExercise(response.data);
      }
    } catch (error) {
      console.error('Error saving exercise:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.data?.message || `Failed to ${selectedExercise ? 'update' : 'create'} exercise`,
        confirmButtonColor: '#FD9C00',
      });
    }
  };

  const handleDeleteExercise = async (exercise) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete the exercise "${exercise.title}"? This will also delete all associated fields.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#FD9C00',
      cancelButtonColor: '#6c757d',
    });

    if (result.isConfirmed) {
      try {
        await deleteExercise(exercise.id).unwrap();
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Exercise has been deleted successfully.',
          timer: 2000,
          showConfirmButton: false,
          confirmButtonColor: '#FD9C00',
        });
        if (selectedExercise?.id === exercise.id) {
          setSelectedExercise(null);
        }
      } catch (error) {
        console.error('Error deleting exercise:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.data?.message || 'Failed to delete exercise',
          confirmButtonColor: '#FD9C00',
        });
      }
    }
  };

  const handleDeleteField = async (field) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete this field?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#FD9C00',
      cancelButtonColor: '#6c757d',
    });

    if (result.isConfirmed) {
      try {
        await deleteField(field.id).unwrap();
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Field has been deleted successfully.',
          timer: 2000,
          showConfirmButton: false,
          confirmButtonColor: '#FD9C00',
        });
      } catch (error) {
        console.error('Error deleting field:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.data?.message || 'Failed to delete field',
          confirmButtonColor: '#FD9C00',
        });
      }
    }
  };

  const handleFieldDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', index);
  };

  const handleFieldDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleFieldDrop = async (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/html'));

    if (dragIndex === dropIndex) return;

    const reorderedFields = [...fields];
    const [draggedField] = reorderedFields.splice(dragIndex, 1);
    reorderedFields.splice(dropIndex, 0, draggedField);

    const reorderData = reorderedFields.map((field, index) => ({
      id: field.id,
      order_index: index,
    }));

    try {
      await reorderFields({ fields: reorderData }).unwrap();
    } catch (error) {
      console.error('Error reordering fields:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to reorder fields',
        confirmButtonColor: '#FD9C00',
      });
    }
  };

  const handleAddField = () => {
    setEditingField(null);
    setShowFieldConfigurator(true);
  };

  const handleEditField = (field) => {
    setEditingField(field);
    setShowFieldConfigurator(true);
  };

  const handleFieldConfiguratorClose = () => {
    setShowFieldConfigurator(false);
    setEditingField(null);
  };

  const handleFieldSuccess = () => {
    // Field was successfully created/updated
    // The query will automatically refetch due to cache invalidation
  };

  const renderFieldPreview = (field) => {
    switch (field.field_type) {
      case 'text':
        return (
          <div className="field-preview">
            <label>{field.label} {field.is_required && <span className="required">*</span>}</label>
            {field.variable_name && <small className="variable-name-badge">Variable: {field.variable_name}</small>}
            <input type="text" placeholder={field.placeholder || ''} disabled />
          </div>
        );
      case 'textarea':
        return (
          <div className="field-preview">
            <label>{field.label} {field.is_required && <span className="required">*</span>}</label>
            {field.variable_name && <small className="variable-name-badge">Variable: {field.variable_name}</small>}
            <textarea placeholder={field.placeholder || ''} rows={3} disabled />
          </div>
        );
      case 'checkbox':
        const checkboxOptions = Array.isArray(field.options) ? field.options : (typeof field.options === 'string' ? JSON.parse(field.options) : []);
        return (
          <div className="field-preview">
            <label>{field.label} {field.is_required && <span className="required">*</span>}</label>
            {field.variable_name && <small className="variable-name-badge">Variable: {field.variable_name}</small>}
            <div className="checkbox-options">
              {checkboxOptions.map((option, idx) => (
                <div key={idx} className="checkbox-option">
                  <input type="checkbox" disabled />
                  <span>{typeof option === 'string' ? option : option.label}</span>
                </div>
              ))}
            </div>
          </div>
        );
      case 'radio':
        const radioOptions = Array.isArray(field.options) ? field.options : (typeof field.options === 'string' ? JSON.parse(field.options) : []);
        return (
          <div className="field-preview">
            <label>{field.label} {field.is_required && <span className="required">*</span>}</label>
            {field.variable_name && <small className="variable-name-badge">Variable: {field.variable_name}</small>}
            <div className="radio-options">
              {radioOptions.map((option, idx) => (
                <div key={idx} className="radio-option">
                  <input type="radio" disabled />
                  <span>{typeof option === 'string' ? option : option.label}</span>
                </div>
              ))}
            </div>
          </div>
        );
      case 'dropdown':
        const dropdownOptions = Array.isArray(field.options) ? field.options : (typeof field.options === 'string' ? JSON.parse(field.options) : []);
        return (
          <div className="field-preview">
            <label>{field.label} {field.is_required && <span className="required">*</span>}</label>
            {field.variable_name && <small className="variable-name-badge">Variable: {field.variable_name}</small>}
            <select disabled>
              <option>Select an option</option>
              {dropdownOptions.map((option, idx) => (
                <option key={idx}>{typeof option === 'string' ? option : option.label}</option>
              ))}
            </select>
          </div>
        );
      case 'display':
        if (field.component_type === 'static-content') {
          const styleMeta = field.component_metadata || {};
          const styleLabel = { info: 'Info', tip: 'Tip', warning: 'Warning', plain: 'Plain' }[styleMeta.style] || 'Info';
          return (
            <div className="field-preview" style={{ padding: '12px', background: '#eff6ff', borderLeft: '4px solid #3b82f6', borderRadius: '8px', fontSize: '13px', color: '#374151' }}>
              <label>{field.label} <span className="field-type-info">(Static — {styleLabel})</span></label>
              <div style={{ marginTop: '4px', opacity: 0.7 }}>
                <i className="fas fa-info-circle"></i> Instructional text block — no user input
              </div>
            </div>
          );
        }
        return (
          <div className="field-preview display-only-preview">
            <label>{field.label}</label>
            <div className="display-info">
              <i className="fas fa-eye"></i>
              <span>Displays value from Step {field.source_step} → {field.variable_name}</span>
            </div>
            {field.placeholder && <small className="placeholder-text">Placeholder: {field.placeholder}</small>}
          </div>
        );
      case 'button': {
        const meta = field.component_metadata || {};
        const btnType = meta.button_type || field.component_type || 'button';
        return (
          <div className="field-preview button-preview">
            <label>{field.label} <span className="field-type-info">({btnType})</span></label>
            {field.component_type === 'copy-block' && meta.copy_content && (
              <small className="placeholder-text">Copy content: {meta.copy_content.substring(0, 80)}{meta.copy_content.length > 80 ? '...' : ''}</small>
            )}
            {field.component_type === 'link-button' && meta.external_url && (
              <small className="placeholder-text">URL: {meta.external_url}</small>
            )}
            {field.component_type === 'download-button' && meta.file_url && (
              <small className="placeholder-text">File: {meta.file_url}</small>
            )}
            <button type="button" disabled style={{ marginTop: '8px', padding: '8px 16px', borderRadius: '6px', border: '1px solid #FD9C00', background: '#FD9C00', color: '#fff', fontSize: '13px', cursor: 'not-allowed', opacity: 0.7 }}>
              {field.label || 'Button'}
            </button>
          </div>
        );
      }
      case 'file':
        return (
          <div className="field-preview file-preview">
            <label>{field.label} {field.is_required && <span className="required">*</span>}</label>
            <div style={{ padding: '16px', border: '2px dashed #dee2e6', borderRadius: '8px', textAlign: 'center', color: '#6c757d', fontSize: '14px' }}>
              <i className="fas fa-cloud-upload-alt" style={{ fontSize: '20px', marginBottom: '4px', display: 'block' }}></i>
              File Upload
            </div>
          </div>
        );
      case 'repeater': {
        const repeaterMeta = field.component_metadata || {};
        const subFields = repeaterMeta.fields || [];
        return (
          <div className="field-preview repeater-preview">
            <label>{field.label} {field.is_required && <span className="required">*</span>}</label>
            {field.variable_name && <small className="variable-name-badge">Variable: {field.variable_name}</small>}
            <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px', fontSize: '13px', color: '#6c757d' }}>
              <i className="fas fa-layer-group"></i> Repeater with {subFields.length} sub-field{subFields.length !== 1 ? 's' : ''}
              {subFields.map((sf, i) => (
                <div key={i} style={{ marginTop: '4px', paddingLeft: '16px' }}>
                  • {sf.label || sf.variable_name || `Field ${i + 1}`} ({sf.type || 'text-input'})
                </div>
              ))}
            </div>
          </div>
        );
      }
      case 'list': {
        const listMeta = field.component_metadata || {};
        return (
          <div className="field-preview list-preview">
            <label>{field.label}</label>
            <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px', fontSize: '13px', color: '#6c757d' }}>
              <i className="fas fa-list"></i> {field.component_type === 'connect-list' ? 'Connect List' : 'List View'}
              {listMeta.data_source && <span> — Source: {listMeta.data_source}</span>}
            </div>
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} centered size="lg">
        <Modal.Body className="exercise-builder-modal-body">
          <div className="exercise-builder-container">
            <div className="exercise-builder-header">
              <h5>Exercise Management</h5>
              <button className="btn-close-modal" onClick={onHide}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="exercise-builder-content">
              {/* Left Panel - Exercise List */}
              <div className="exercise-list-panel">
                <div className="panel-header">
                  <h6>Exercises</h6>
                  <button className="btn-new-exercise" onClick={handleNewExercise}>
                    <i className="fas fa-plus"></i> New
                  </button>
                </div>
                <div className="exercise-list">
                  {exercisesLoading ? (
                    <div className="loading-text">Loading exercises...</div>
                  ) : exercises.length === 0 ? (
                    <div className="empty-state-text">No exercises yet. Create your first exercise.</div>
                  ) : (
                    exercises.map((exercise) => (
                      <div
                        key={exercise.id}
                        className={`exercise-list-item ${selectedExercise?.id === exercise.id ? 'active' : ''}`}
                        onClick={() => setSelectedExercise(exercise)}
                      >
                        <div className="exercise-item-content">
                          <h6>{exercise.title}</h6>
                          {exercise.description && <p>{exercise.description}</p>}
                        </div>
                        <div className="exercise-item-actions">
                          <button
                            className="btn-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditExercise(exercise);
                            }}
                            title="Edit"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            className="btn-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteExercise(exercise);
                            }}
                            title="Delete"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Right Panel - Exercise Details & Fields */}
              <div className="exercise-details-panel">
                {isEditingExercise ? (
                  <div className="exercise-form">
                    <h6>{selectedExercise ? 'Edit Exercise' : 'New Exercise'}</h6>
                    <form onSubmit={handleExerciseSubmit}>
                      <div className="form-group">
                        <label>
                          Exercise Title <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          value={exerciseFormData.title}
                          onChange={(e) => setExerciseFormData({ ...exerciseFormData, title: e.target.value })}
                          placeholder="Enter exercise title"
                          className={errors.title ? 'is-invalid' : ''}
                          maxLength={500}
                        />
                        {errors.title && <p className="error-text">{errors.title}</p>}
                      </div>
                      <div className="form-group">
                        <label>Description</label>
                        <textarea
                          value={exerciseFormData.description}
                          onChange={(e) => setExerciseFormData({ ...exerciseFormData, description: e.target.value })}
                          placeholder="Enter exercise description (optional)"
                          rows={4}
                        />
                      </div>
                      <div className="form-actions">
                        <button type="button" className="btn-outline" onClick={handleCancelEdit} disabled={isLoading}>
                          Cancel
                        </button>
                        <button type="submit" className="btn-primary" disabled={isLoading}>
                          {isLoading ? 'Saving...' : 'Save Exercise'}
                        </button>
                      </div>
                    </form>
                  </div>
                ) : selectedExercise ? (
                  <div className="exercise-details">
                    <div className="details-header">
                      <div>
                        <h6>{selectedExercise.title}</h6>
                        {selectedExercise.description && <p>{selectedExercise.description}</p>}
                      </div>
                      <button className="btn-edit" onClick={() => handleEditExercise(selectedExercise)}>
                        <i className="fas fa-edit"></i> Edit
                      </button>
                    </div>

                    <div className="fields-section">
                      <div className="fields-header">
                        <h6>Form Fields</h6>
                        <button className="btn-add-field" onClick={handleAddField}>
                          <i className="fas fa-plus"></i> Add Field
                        </button>
                      </div>
                      <div className="fields-list">
                        {fields.length === 0 ? (
                          <div className="empty-state-text">No fields yet. Add fields to this exercise.</div>
                        ) : (
                          fields.map((field, index) => (
                            <div
                              key={field.id}
                              className="field-item"
                              draggable
                              onDragStart={(e) => handleFieldDragStart(e, index)}
                              onDragOver={handleFieldDragOver}
                              onDrop={(e) => handleFieldDrop(e, index)}
                            >
                              <div className="field-item-header">
                                <div className="field-drag-handle">
                                  <i className="fas fa-grip-vertical"></i>
                                </div>
                                <div className="field-type-badge">{field.component_type || field.field_type}</div>
                                <div className="field-actions" style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                                  <button
                                    className="btn-icon-small"
                                    onClick={() => handleEditField(field)}
                                    title="Edit this field"
                                    style={{
                                      background: 'none',
                                      border: '1px solid #FD9C00',
                                      color: '#FD9C00',
                                      cursor: 'pointer',
                                      padding: '4px 12px',
                                      fontSize: '12px',
                                      borderRadius: '4px',
                                      fontWeight: '600'
                                    }}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    className="btn-icon-small"
                                    onClick={() => handleDeleteField(field)}
                                    title="Delete this field"
                                    style={{
                                      background: 'none',
                                      border: '1px solid #dc3545',
                                      color: '#dc3545',
                                      cursor: 'pointer',
                                      padding: '4px 12px',
                                      fontSize: '12px',
                                      borderRadius: '4px',
                                      fontWeight: '600'
                                    }}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                              {renderFieldPreview(field)}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="empty-selection">
                    <i className="fas fa-clipboard-list"></i>
                    <p>Select an exercise to view and manage its fields</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <FieldConfigurator
        show={showFieldConfigurator}
        onHide={handleFieldConfiguratorClose}
        exerciseId={selectedExercise?.id}
        field={editingField}
        onSuccess={handleFieldSuccess}
        currentStepNumber={currentStepNumber}
      />
    </>
  );
};

export default ExerciseBuilder;
